import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify"; // Import toast
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { Handler } from "leaflet";
import { FaInfo, FaInfoCircle } from "react-icons/fa";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddProduct() {
  const [productName, setProductName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [description, setDescription] = useState("");
  const [selectedUnit, setSelectedUnit] = useState(""); // New state for selected unit
  const [selectedImage, setSelectedImage] = useState([]);
  const [imagePreview, setImagePreview] = useState(null); // New state for preview
  const [units, setUnits] = useState([]); // State to hold units fetched from API
  const [productCode, setProductCode] = useState(""); // State for product code
  const [showProductCode, setShowProductCode] = useState(false); // State to control visibility of product code\
  const [allowPartialQuantity, setAllowPartialQuantity] = useState(true); // state to allow partial quantity allocation
  const [allowQuantityModification, setAllowQuantityModification] = useState(true); // state to control quantity modification
  const [allowSalesOrderInclusive, setAllowSalesOrderInclusive] = useState(true); //state to control sales order inclusive

  useEffect(() => {
    // Fetch units from the API when the component mounts
    const fetchUnits = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/units`);
        setUnits(response.data.units); // Assuming the response has a `units` field
      } catch (error) {
        console.error("Error fetching units:", error);
        toast.error("Error fetching units"); // Display error toast
      }
    };

    // Fetch categories from the API when the component mounts
    const fetchCategories = async () => {
      try {
        const token = sessionStorage.getItem("token"); // Assuming token is saved in sessionStorage
    
        if (!token) {
          toast.error("No token found. Please log in.");
          return;
        }
    
        const response = await axios.get(`${API_BASE_URL}/categories`, {
          headers: {
            Authorization: `Bearer ${token}`, // Include token in the Authorization header
          },
        });
    
        // Assuming the response contains categories related to the user's organization
        setCategories(response.data.categories); // Set the filtered categories based on the user's organization
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Error fetching categories");
      }
    };

    fetchUnits();
    fetchCategories();

    // Generate and set product code on component mount
    setProductCode(generateProductCode());
  }, []);

  const generateProductCode = () => {
    return "PROD-" + Math.random().toString(36).substr(2, 9).toUpperCase();
  };



  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file)); // Generate preview URL
    } else {
      setSelectedImage(null);
      setImagePreview(null);
    }
  };

  const handlePartialQuantityChange = (event) => {
    setAllowPartialQuantity(event.target.checked);
  };  

  const handleQuantityModification = (event) => {
    setAllowQuantityModification(event.target.checked);
  };

  const handleSalesOrderInclusive = (event) => {
    setAllowSalesOrderInclusive(event.target.checked);
  }

  useEffect(() => {
  }, [allowPartialQuantity]);

  const handleAddProduct = async () => {
    // Validate required fields
    if (
      !productName ||
      !selectedCategory ||
      !description ||
      !selectedUnit ||
      !selectedImage
    ) {
      toast.error("All fields are required.");
      return;
    }

    // Prepare FormData
    const formData = new FormData();
    formData.append("product_name", productName);
    formData.append("product_category", selectedCategory);
    formData.append("product_description", description);
    formData.append("unit", selectedUnit);
    formData.append("productCode", productCode); // Add product code to form data
    formData.append("product_image", selectedImage); // Append the image file
    formData.append("allowPartialQuantity", allowPartialQuantity); //append the checkbox value
    formData.append("allowQuantityModification", allowQuantityModification); //append the checkbox value
    formData.append("allowSalesOrderInclusive", allowSalesOrderInclusive); //append the checkbox value

    const apiUrl = `${API_BASE_URL}/products/add`;

    try {
      const token = sessionStorage.getItem("token"); // Retrieve token from sessionStorage
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      // Axios POST request
      const response = await axios.post(apiUrl, formData, {
        headers: {
          Authorization: `Bearer ${token}`, // Include token in Authorization header
          // Do NOT set Content-Type manually here!
        },
      });

      const { message, product } = response.data;

      // Update product code if returned
      if (product && product.product_code) {
        setProductCode(product.product_code);
        setShowProductCode(true);
      }
      toast.success(message || "Product added successfully!");

      // Navigate back to Products.js after successful addition
      // navigate("/products"); // Adjust the route based on your setup
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const { message, details } = error.response.data;

        if (details?.organization_name) {
          // Show specific error for duplicate product in another organization
          toast.error(
            `${message} (Organization: ${details.organization_name})`
          );
        } else {
          // Generic error message
          toast.error(message || "Error adding product.");
        }
      } else {
        toast.error("Failed to add product. Please try again.");
      }
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Add Product</h2>
        <div className="grid grid-cols-2 gap-8">
          <div>
            {/* Product Code Display */}
            {showProductCode && (
              <div className="col-span-2 mt-4">
                <p className="text-xl font-semibold">
                  Product Code: {productCode}
                </p>
              </div>
            )}
            {/* Product Name Input */}
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Product Name</label>
              <input
                type="text"
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter product name"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
              />
              <p className="text-sm text-gray-500 mt-1">
                Do not exceed 25 characters when entering the product name.
              </p>
            </div>
            {/* Category Select */}
            <div className="mb-4">
              <label className="w-full block text-gray-700 mb-2">
                Category
              </label>
              <select
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Select Product Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category.category_name}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>
            {/* Description */}
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Description</label>
              <textarea
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter product description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Product Image</label>
              <input
                type="file"
                accept="image/*"
                className="w-full p-3 border rounded-lg focus:outline-none"
                onChange={handleImageChange} // Use the new handler
              />
            </div>
            {/* Unit Select */}
            <div className="mb-4">
              <label className="w-full block text-gray-700 mb-2">Unit</label>
              <select
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                value={selectedUnit}
                onChange={(e) => setSelectedUnit(e.target.value)}
              >
                <option value="">Select Product Unit</option>
                {units.map((unit) => (
                  <option key={unit._id} value={unit.unit_name}>
                    {unit.unit_name}
                  </option>
                ))}
              </select>
            </div>
              {/* Partial Quantity Allocation */}
              <div className="mb-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowPartialQuantity}
                    onChange={handlePartialQuantityChange}
                    color="primary"
                  />
                }
                label={
                  <>
                    Allow Partial Quantities?{"  "}
                    <span style={{ fontStyle: "italic", color: "gray", fontSize: "medium" }}> (eg: 50.5kg) </span>
                  </>
                }
              />
            </div>
            {/* Quantity Modification Allocation */}
              <div className="mb-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowQuantityModification}
                    onChange={handleQuantityModification}
                    color="primary"
                  />
                }
                label={
                  <Tooltip
                    title={
                      <span style={{fontSize: '14px'}}>
                        Allows user to approve more than the reqeusted quantity by the customer.
                      </span>
                    }>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        Allow Quantity Modification?&nbsp;<FaInfoCircle style={{color: "gray", marginLeft: '4px' }} />
                    </span>
                  </Tooltip>
                }
              />
            </div>
            {/* Sales Order Inclusive Allocation */}
              <div className="mb-4">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowSalesOrderInclusive}
                    onChange={handleSalesOrderInclusive}
                    color="primary"
                  />
                }
                label={
                  <Tooltip 
                    title={
                      <span style={{ fontSize: '14px' }}>
                        These are optional products that can be directly added when creating a Sales Order.
                      </span>}>
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        Additional Product&nbsp;<FaInfoCircle style={{color: "gray", marginLeft: '4px' }} />
                      </span>
                  </Tooltip>
                }
              />
            </div>
          </div>
          {/* Image Preview */}
          <div className="flex justify-center items-center border rounded-lg p-4">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Product Preview"
                className="w-64 h-64 object-cover"
              />
            ) : (
              <p className="text-gray-500">Image Preview will appear here</p>
            )}
          </div>
        </div>
        <div className="mt-6 text-right">
          <button
            onClick={handleAddProduct}
            className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300"
          >
            Add Product
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddProduct;
